import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Privacy Compliance
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography>Privacy Compliance</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            The Privacy Compliance Overview section is designed to encapsulate our comprehensive approach to ensuring
            adherence to the Health Insurance Portability and Accountability Act (HIPAA), particularly focusing on the
            privacy aspects. This documentation is critical for maintaining the integrity and confidentiality of
            Protected Health Information (PHI), which is paramount to fostering trust and compliance in our SaaS medical
            application environment. Detailed herein are the policies, procedures, and measures implemented to safeguard
            patient privacy, alongside our strategies for workforce training and management in compliance with HIPAA
            mandates.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Privacy Compliance Overview
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Notice of Privacy Practices:</b> A cornerstone document that communicates to patients how their PHI
                  is utilized and disclosed in adherence to privacy standards, outlining their rights regarding their
                  health information.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Patient Rights Policy:</b> This policy ensures patients have access to, and control over, their
                  PHI, including the rights to amend and manage the privacy of their health information as stipulated by
                  HIPAA.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Minimum Necessary Use and Disclosure:</b> Outlines the principle that PHI disclosures are limited
                  to the minimal necessary information required for the intended purpose, ensuring stringent privacy
                  protection.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Authorization Requirements:</b> Details the procedures and conditions under which written
                  authorization is required from patients before their PHI can be used or disclosed for purposes beyond
                  treatment, payment, or healthcare operations.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Privacy Training and Awareness:</b> Highlights the importance of educating our workforce on privacy
                  policies and procedures, ensuring they understand the gravity of PHI protection and the legal
                  implications of non-compliance.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Key Goals
          </Typography>
          <List>
            <ListItem>
              To systematically document and communicate our privacy policies and procedures to meet and exceed HIPAA
              privacy standards.
            </ListItem>
            <ListItem>
              To implement an effective privacy training program for our employees, ensuring they are well-versed in the
              protection of PHI and HIPAA compliance.
            </ListItem>
            <ListItem>
              To affirm our unwavering commitment to the privacy and security of patient information, reinforcing our
              position as a trusted entity in the healthcare technology sector.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
